import { BellIcon } from '@phosphor-icons/react/dist/csr/Bell';
import { CircleHalfIcon } from '@phosphor-icons/react/dist/csr/CircleHalf';
import { ImageSquareIcon } from '@phosphor-icons/react/dist/csr/ImageSquare';
import { PaletteIcon } from '@phosphor-icons/react/dist/csr/Palette';
import { RowsIcon } from '@phosphor-icons/react/dist/csr/Rows';
import { SidebarSimpleIcon } from '@phosphor-icons/react/dist/csr/SidebarSimple';
import { TranslateIcon } from '@phosphor-icons/react/dist/csr/Translate';
import { UserCircleIcon } from '@phosphor-icons/react/dist/csr/UserCircle';
import { useTranslation } from 'react-i18next';

import { Button } from '../../design-system/primitives/Button/Button';
import { CountBadge } from '../../design-system/primitives/CountBadge/CountBadge';
import { BACKGROUND_OPTIONS } from '../../design-system/backgrounds';
import { ACCENTS, useAppearance } from '../../design-system/providers/appearance';
import { Breadcrumb } from './Breadcrumb';
import { ShellMenu } from './ShellMenu';
import type { BreadcrumbItem } from './shellState';
import styles from './Topbar.module.css';

/**
 * The topbar. Handoff: "sidebar toggle, breadcrumb, then locale switch, density
 * switch, notifications. End padding >= 26px so the notification badge clears
 * the edge."
 *
 * This is where the three global switches finally get UI. They read and write
 * `useAppearance()` — the single owner of `data-theme`, `data-density`, `lang`
 * and `dir` — rather than touching the document themselves, which is what keeps
 * the direction of the page and the language of its words from disagreeing.
 *
 * The notification button's accessible NAME carries the count:
 * "Notifications 7 unread". The alternative — `aria-label="Notifications"` with
 * a badge inside — silently drops the number, because `aria-label` outranks
 * content. Building the name from a visually-hidden span plus the badge's own
 * hidden label means the name changes when the count changes, which is both the
 * correct behaviour and the only version a test can kill.
 */

export interface TopbarProps {
  breadcrumb?: readonly BreadcrumbItem[];
  notificationCount?: number;
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  /** The id of the `<nav>` the toggle controls, for `aria-controls`. */
  sidebarId: string;
  onNavigate?: (path: string) => void;
  onNotificationsClick?: () => void;
  /**
   * Sign out. The account menu renders only when this is supplied — the shell
   * is used by tests and by the design harness with no session behind it, and
   * an account menu whose Sign out does nothing is worse than no menu.
   */
  onSignOut?: () => void;
  className?: string;
}

export function Topbar({
  breadcrumb = [],
  notificationCount = 0,
  sidebarCollapsed,
  onToggleSidebar,
  sidebarId,
  onNavigate,
  onNotificationsClick,
  onSignOut,
  className,
}: TopbarProps) {
  const { t } = useTranslation();
  const {
    theme,
    density,
    locale,
    accent,
    background,
    setTheme,
    setDensity,
    setLocale,
    setAccent,
    setBackground,
  } = useAppearance();

  return (
    <header className={[styles['topbar'], className].filter(Boolean).join(' ')}>
      <Button
        variant="icon"
        icon={<SidebarSimpleIcon />}
        data-testid="sidebarToggle"
        aria-expanded={!sidebarCollapsed}
        aria-controls={sidebarId}
        onClick={onToggleSidebar}
      >
        {/*
          The name stays "Toggle the sidebar" in both states. APG: when
          `aria-expanded` carries the state, a name that also changes is read
          twice and shifts under the user mid-interaction.
        */}
        <span className={styles['srOnly']}>{t('a11y.toggleSidebar')}</span>
      </Button>

      <Breadcrumb items={breadcrumb} onNavigate={onNavigate} className={styles['breadcrumb']} />

      <div className={styles['actions']}>
        <ShellMenu
          label={t('a11y.localeSwitch')}
          icon={<TranslateIcon />}
          testId="localeSwitch"
          value={locale}
          options={[
            { value: 'en', label: t('locale.english') },
            { value: 'ar', label: t('locale.arabic') },
          ]}
          onSelect={setLocale}
        />
        <ShellMenu
          label={t('a11y.densitySwitch')}
          icon={<RowsIcon />}
          testId="densitySwitch"
          value={density}
          options={[
            { value: 'comfortable', label: t('density.comfortable') },
            { value: 'compact', label: t('density.compact') },
          ]}
          onSelect={setDensity}
        />
        <ShellMenu
          label={t('a11y.themeSwitch')}
          icon={<CircleHalfIcon />}
          testId="themeSwitch"
          value={theme}
          options={[
            { value: 'dark', label: t('theme.dark') },
            { value: 'light', label: t('theme.light') },
          ]}
          onSelect={setTheme}
        />
        <ShellMenu
          label={t('a11y.accentSwitch')}
          icon={<PaletteIcon />}
          testId="accentSwitch"
          value={accent}
          options={ACCENTS.map((value) => ({
            value,
            label: t(`accent.${value}`),
            /*
              The swatch carries `data-accent` itself, so the token sheet's own
              palette block for that value repaints `--color-accent` inside it.
              No colour is restated here: the sheet stays the only place a
              palette's hex lives.
            */
            visual: <span className={styles['swatch']} data-accent={value} />,
          }))}
          onSelect={setAccent}
        />
        <ShellMenu
          label={t('a11y.backgroundSwitch')}
          icon={<ImageSquareIcon />}
          testId="backgroundSwitch"
          layout="thumbnails"
          value={background}
          options={BACKGROUND_OPTIONS.map((option) => ({
            value: option.id,
            label: t(option.labelKey),
            visual:
              option.url === null ? (
                <span className={styles['thumbNone']} />
              ) : (
                <img className={styles['thumb']} src={option.url} alt="" loading="lazy" />
              ),
          }))}
          onSelect={setBackground}
        />

        <Button
          variant="icon"
          icon={<BellIcon />}
          data-testid="notifications"
          className={styles['bell']}
          onClick={onNotificationsClick}
        >
          <span className={styles['srOnly']}>{t('a11y.notifications')}</span>
          {/*
            Handoff, migration map: "Permanent red '0' notification badge" ->
            "Badge hidden at zero". CountBadge returns null at zero, so the
            testid disappears with it.
          */}
          <CountBadge
            count={notificationCount}
            tone="alert"
            label={t('a11y.unreadNotifications', { count: notificationCount })}
            data-testid="notification-badge"
            className={styles['bellBadge']}
          />
        </Button>

		{onSignOut === undefined ? null : (
          <ShellMenu
            label={t('a11y.accountMenu')}
            icon={<UserCircleIcon />}
            testId="accountMenu"
            /*
              NO `value`. These are commands, not a set of values to pick from —
              see ShellMenu's own `value` docblock for why that one omission is
              what turns `menuitemradio` into `menuitem` and drops the
              `aria-checked` a screen reader would otherwise read out over
              "Sign out".
            */
            options={[
              { value: 'settings', label: t('nav.settings') },
              { value: 'signOut', label: t('auth.signOut') },
            ]}
            onSelect={(command) => {
              if (command === 'signOut') {
                onSignOut();
                return;
              }
              // `/profile`, matching `navigation.ts`'s own `settings` entry
              // rather than a second literal for the same screen.
              onNavigate?.('/profile');
            }}
          />
        )}
      </div>
    </header>
  );
}
